import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaSpinner,
  FaClock,
  FaCheckCircle,
  FaUser,
  FaArrowRight,
  FaGraduationCap,
  FaCode,
  FaBrain,
  FaRocket,
  FaBookOpen,
  FaMapSigns,
} from "react-icons/fa";
import axios from "../utils/axiosConfig";
import toast from "react-hot-toast";
import Loader from "../components/common/Loader";
import { useFooter } from "../context/FooterContext";

const LearningRoadmap = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { setShowFooter } = useFooter();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [enrolling, setEnrolling] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    setShowFooter(false);
    fetchDomains();
    checkDarkMode();
    return () => setShowFooter(true);
  }, [isAuthenticated]);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const fetchDomains = async () => {
    setLoading(true);
    try {
      const response = await axios.get("/api/roadmap/domains");
      setDomains(response.data.domains);
    } catch (error) {
      console.error("Fetch domains error:", error);
      toast.error("Failed to fetch domains");
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (domainId, domainName) => {
    setEnrolling(domainId);
    try {
      const response = await axios.post(`/api/roadmap/enroll/${domainId}`);
      if (response.data.success) {
        toast.success(`Enrolled in ${domainName}!`);
        fetchDomains(); // Refresh to show updated enrollment status
      }
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("Already enrolled in this course");
      } else {
        toast.error("Failed to enroll");
      }
    } finally {
      setEnrolling(null);
    }
  };

  const handleViewCourse = (domainId) => {
    navigate(`/learning/course/${domainId}`);
  };

  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";

  if (loading) return <Loader />;

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{
          background: "linear-gradient(135deg, #1e1b4b 0%, #2e1065 100%)",
          padding: "2rem",
          borderRadius: "24px",
          marginBottom: "2rem",
          color: "white",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "1rem",
            marginBottom: "1rem",
          }}
        >
          <FaMapSigns size={32} />
          <h1 style={{ fontSize: "1.8rem", margin: 0 }}>Browse Courses</h1>
        </div>
        <p style={{ opacity: 0.9 }}>
          Explore all available learning paths. Enroll in courses that interest
          you.
        </p>
      </motion.div>

      {/* Courses Grid - ALL COURSES with Enroll buttons */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: "1.5rem",
        }}
      >
        {domains.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "3rem",
              color: getTextSecondary(),
            }}
          >
            No courses available yet. Check back soon!
          </div>
        ) : (
          domains.map((domain) => (
            <motion.div
              key={domain._id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              whileHover={{ y: -5 }}
              style={{
                background: getCardBackground(),
                borderRadius: "20px",
                padding: "1.5rem",
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              <div
                style={{
                  width: "60px",
                  height: "60px",
                  background: `${domain.color}20`,
                  borderRadius: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1rem",
                }}
              >
                <FaCode size={28} color={domain.color} />
              </div>
              <h3 style={{ color: getTextColor(), marginBottom: "0.5rem" }}>
                {domain.name}
              </h3>
              <p
                style={{
                  color: getTextSecondary(),
                  fontSize: "0.85rem",
                  marginBottom: "1rem",
                  minHeight: "60px",
                }}
              >
                {domain.description?.substring(0, 100)}...
              </p>

              <div style={{ marginBottom: "1rem" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "0.3rem",
                  }}
                >
                  <span
                    style={{ fontSize: "0.7rem", color: getTextSecondary() }}
                  >
                    📚 {domain.totalTopics} topics
                  </span>
                  {domain.isEnrolled && (
                    <span style={{ fontSize: "0.7rem", color: "#10b981" }}>
                      ✅ Enrolled
                    </span>
                  )}
                </div>

                {domain.isEnrolled ? (
                  <>
                    <div
                      style={{
                        height: "6px",
                        background: isDarkMode
                          ? "rgba(255,255,255,0.1)"
                          : "#e2e8f0",
                        borderRadius: "5px",
                        overflow: "hidden",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <div
                        style={{
                          width: `${domain.progress.percentage}%`,
                          height: "100%",
                          background: domain.color,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <span
                        style={{
                          color: getTextSecondary(),
                          fontSize: "0.7rem",
                        }}
                      >
                        {domain.progress.completed} / {domain.totalTopics}{" "}
                        completed
                      </span>
                      <span
                        style={{
                          color: domain.color,
                          fontSize: "0.7rem",
                          fontWeight: "500",
                        }}
                      >
                        {Math.round(domain.progress.percentage)}%
                      </span>
                    </div>
                  </>
                ) : (
                  <div style={{ height: "24px" }} /> // Spacer for alignment
                )}
              </div>

              {domain.isEnrolled ? (
                <button
                  onClick={() => handleViewCourse(domain._id)}
                  style={{
                    width: "100%",
                    padding: "0.6rem",
                    background: "linear-gradient(135deg, #10b981, #059669)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontWeight: "500",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.5rem",
                  }}
                >
                  Continue Learning <FaArrowRight />
                </button>
              ) : (
                <button
                  onClick={() => handleEnroll(domain._id, domain.name)}
                  disabled={enrolling === domain._id}
                  style={{
                    width: "100%",
                    padding: "0.6rem",
                    background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                    color: "white",
                    border: "none",
                    borderRadius: "10px",
                    cursor: "pointer",
                    fontWeight: "500",
                    opacity: enrolling === domain._id ? 0.7 : 1,
                  }}
                >
                  {enrolling === domain._id ? "Enrolling..." : "Enroll Now"}
                </button>
              )}
            </motion.div>
          ))
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default LearningRoadmap;
